// Aviation Control Tower components shared between PassportTrackerPage and PublicTrackerPage
// Theme: obsidian black + monospace + neon accents + scan lines + glass bento
import { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw, Wifi, WifiOff, Search, X, Sparkles,
  Clock, Globe2, BarChart3, Activity, AlertTriangle, RotateCcw, CheckCircle
} from 'lucide-react';

/* ===========================================================================
   OpsBackground – obsidian bg with hex grid + scan-lines + brand glow
=========================================================================== */
export const OpsBackground = ({ children }) => (
  <div className="ops-shell relative min-h-screen overflow-hidden text-white"
       style={{ background: 'radial-gradient(1200px 600px at 80% -10%, rgba(245,158,11,0.06), transparent 60%), radial-gradient(900px 500px at -10% 110%, rgba(16,185,129,0.05), transparent 60%), #050810' }}>
    {/* hex / dot grid */}
    <div className="pointer-events-none absolute inset-0 opacity-[0.05]"
         style={{
           backgroundImage: 'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)',
           backgroundSize: '24px 24px'
         }} />
    {/* faint scanlines */}
    <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
         style={{
           backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.3) 0px, rgba(255,255,255,0.3) 1px, transparent 1px, transparent 3px)'
         }} />
    {/* film grain */}
    <div className="pointer-events-none absolute inset-0 opacity-[0.03] mix-blend-overlay"
         style={{
           backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
         }} />
    {/* Global Ops keyframes (hoisted, defined once) */}
    <style>{`
      @keyframes opsScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
      .ops-fids-track { display: inline-block; padding-left: 100%; animation: opsScroll 60s linear infinite; }
      @keyframes ops-spin-slow { 0% { transform: rotate(0); } 100% { transform: rotate(360deg); } }
      .ops-spin-slow { animation: ops-spin-slow 3.5s linear infinite; }
    `}</style>
    <div className="relative z-10">{children}</div>
  </div>
);

/* ===========================================================================
   OpsStatusBar – top live ops bar with clock + connection + scrolling FIDS
=========================================================================== */
export const OpsStatusBar = ({ accountingStatus, stats, refreshing, onRefresh, lastUpdate, sample = [] }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const fmt = now.toLocaleTimeString('en-GB', { hour12: false, timeZone: 'Asia/Dubai' });
  const connected = !!accountingStatus?.connected;

  return (
    <div className="relative">
      {/* Top bar */}
      <div className="border-b border-white/5 bg-black/60 backdrop-blur-md">
        <div className="flex items-center justify-between gap-4 px-4 sm:px-6 h-10">
          <div className="flex items-center gap-3 text-[10px] sm:text-[11px] font-mono tracking-wider uppercase text-emerald-300/90 truncate">
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="hidden sm:inline">TADABER OPS</span>
            <span className="text-white/30 hidden sm:inline">//</span>
            <span className="text-white/70 hidden md:inline">PASSPORT TRACKER</span>
            <span className="text-white/30 hidden md:inline">//</span>
            <span className={connected ? 'text-emerald-300' : 'text-red-300'}>
              {connected ? 'LIVE FEED ACTIVE' : 'FEED OFFLINE'}
            </span>
            <span className="text-white/30">//</span>
            <span className="text-amber-300">{fmt} GST</span>
          </div>

          <div className="flex items-center gap-2 text-[10px] font-mono">
            <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${connected ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-red-500/40 bg-red-500/10 text-red-300'}`}>
              {connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              <span className="hidden sm:inline">{connected ? 'TADABER CONNECTED' : 'DISCONNECTED'}</span>
              {connected && (
                <span className="text-emerald-300/70">· {accountingStatus?.contracts_matched ?? 0} contracts</span>
              )}
            </span>
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className="h-6 w-6 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 hover:text-white transition"
              title="Refresh"
              data-testid="ops-refresh-btn"
            >
              <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* FIDS scrolling strip */}
      {sample.length > 0 && (
        <div className="border-b border-white/5 bg-black/40 overflow-hidden">
          <div className="ops-fids-track py-1.5 text-[11px] font-mono tracking-wide whitespace-nowrap">
            {[...sample, ...sample].map((s, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-3 text-white/50">
                <span className="h-1 w-1 rounded-full bg-white/30" />
                <span className="text-white/70">{s.flag || '🌍'}</span>
                <span className="text-white/80">{s.name}</span>
                <span className="text-white/30">·</span>
                <span className="font-bold text-white/90">{s.passport}</span>
                <span className="text-white/30">·</span>
                <span className={s.statusColor}>{s.status}</span>
                {s.extra && <><span className="text-white/30">·</span><span className="text-white/60">{s.extra}</span></>}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};/* ===========================================================================
   KpiTile – single tile with monospace number + sparkline
=========================================================================== */
const Sparkline = ({ data = [3, 5, 4, 6, 5, 8, 7, 9, 7, 10], color = '#10B981', height = 28 }) => {
  const max = Math.max(...data, 1);
  const points = data.map((v, i) => `${(i / (data.length - 1)) * 100},${100 - (v / max) * 90 - 5}`).join(' ');
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full" style={{ height }}>
      <defs>
        <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.5" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <polygon points={`${points} 100,100 0,100`} fill={`url(#grad-${color.replace('#', '')})`} />
    </svg>
  );
};

export const KpiTile = ({ label, labelAr, value, color, accent, icon: Icon, sparkline, active, onClick, testId }) => {
  const colorMap = {
    red:     { text: 'text-red-400',     bg: 'bg-red-500/10',     border: 'border-red-500/40',     hex: '#EF4444' },
    emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/40', hex: '#10B981' },
    amber:   { text: 'text-amber-300',   bg: 'bg-amber-500/10',   border: 'border-amber-500/50',   hex: '#F59E0B' },
    cyan:    { text: 'text-cyan-300',    bg: 'bg-cyan-500/10',    border: 'border-cyan-500/40',    hex: '#06B6D4' },
    blue:    { text: 'text-blue-400',    bg: 'bg-blue-500/10',    border: 'border-blue-500/40',    hex: '#3B82F6' },
  }[color] || { text: 'text-white', bg: 'bg-white/5', border: 'border-white/10', hex: '#94A3B8' };

  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative group text-left rounded-2xl border ${colorMap.border} ${colorMap.bg} backdrop-blur-sm p-3 sm:p-4 transition-all hover:scale-[1.01] ${active ? 'ring-2 ring-offset-0' : ''}`}
      style={active ? { boxShadow: `0 0 0 1px ${colorMap.hex}, 0 0 24px ${colorMap.hex}33` } : { boxShadow: `0 0 24px ${colorMap.hex}10` }}
    >
      {/* corner brackets */}
      <span className="pointer-events-none absolute top-1.5 left-1.5 h-2 w-2 border-t border-l border-white/30" />
      <span className="pointer-events-none absolute top-1.5 right-1.5 h-2 w-2 border-t border-r border-white/30" />
      <span className="pointer-events-none absolute bottom-1.5 left-1.5 h-2 w-2 border-b border-l border-white/30" />
      <span className="pointer-events-none absolute bottom-1.5 right-1.5 h-2 w-2 border-b border-r border-white/30" />

      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-white/60">{label}</span>
        {Icon && <Icon className={`h-4 w-4 ${colorMap.text} ${color === 'amber' ? 'ops-spin-slow' : ''}`} />}
      </div>
      <div className="flex items-end gap-2">
        <span className={`text-3xl sm:text-4xl font-black font-mono tabular-nums leading-none ${colorMap.text}`} style={{ textShadow: `0 0 16px ${colorMap.hex}55` }}>
          {value}
        </span>
        {sparkline && <div className="flex-1 max-w-[80px] opacity-70"><Sparkline data={sparkline} color={colorMap.hex} /></div>}
      </div>
      {labelAr && <p className="mt-1.5 text-[10px] tracking-wider text-white/50 font-mono">{accent} <span className="text-white/40">/ {labelAr}</span></p>}
    </button>
  );
};

/* ===========================================================================
   BentoSidebar – right column widgets: refund timeline, nationality, urgency
=========================================================================== */
export const BentoSidebar = ({ data }) => {
  const items = data?.pending || [];

  // Nationality breakdown
  const breakdown = useMemo(() => {
    const map = {};
    [...(data?.pending || []), ...(data?.sent || [])].forEach((p) => {
      const nat = (p.nationality || 'OTHER').toUpperCase();
      map[nat] = (map[nat] || 0) + 1;
    });
    const total = Object.values(map).reduce((a, b) => a + b, 0) || 1;
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([nat, c]) => ({
        nat,
        flag: nat.includes('PHIL') ? '🇵🇭' : nat.includes('INDO') ? '🇮🇩' : nat.includes('KEN') ? '🇰🇪' : nat.includes('BANG') ? '🇧🇩' : nat.includes('ETHI') ? '🇪🇹' : nat.includes('UGAN') ? '🇺🇬' : '🌍',
        pct: Math.round((c / total) * 100),
        count: c,
      }));
  }, [data]);

  // Urgency heatmap (7 days × pending count buckets)
  const urgency = useMemo(() => {
    const buckets = [0, 7, 14, 21, 30, 45, 60];
    const map = buckets.map((b, i) => ({
      label: i === 0 ? '0-6d' : `${b}+`,
      count: items.filter((p) => {
        const d = p.daysSinceEntry || 0;
        const next = buckets[i + 1] || 9999;
        return d >= b && d < next;
      }).length,
    }));
    return map;
  }, [items]);

  // Refund timeline sparkline
  const refundSeries = useMemo(() => {
    const arr = items.filter((p) => p.refund_alert);
    return arr.length > 0 ? [1, 0, 1, 2, 1, 0, arr.length] : [0, 0, 0, 1, 0, 0, 0];
  }, [items]);

  const tileBase = 'rounded-2xl border border-white/8 bg-white/[0.02] backdrop-blur-xl p-4 relative overflow-hidden';

  return (
    <div className="space-y-3">
      {/* GLOBAL FEED */}
      <div className={tileBase}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-white/60 flex items-center gap-1.5"><Globe2 className="h-3 w-3" /> Global Feed</span>
          <span className="text-[10px] font-mono text-cyan-300/70">{items.length} ACTIVE</span>
        </div>
        <div className="relative aspect-[16/9] rounded-lg bg-gradient-to-br from-cyan-500/5 to-transparent border border-cyan-500/20 overflow-hidden">
          {/* simplified world map dots */}
          <svg viewBox="0 0 320 180" className="w-full h-full">
            <defs>
              <radialGradient id="hubglow">
                <stop offset="0%" stopColor="#06B6D4" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#06B6D4" stopOpacity="0" />
              </radialGradient>
            </defs>
            {/* Dubai hub */}
            <circle cx="190" cy="80" r="20" fill="url(#hubglow)" />
            <circle cx="190" cy="80" r="3" fill="#F59E0B" />
            {/* arcs */}
            {[
              { x: 240, y: 95, label: 'MNL' },
              { x: 230, y: 110, label: 'JKT' },
              { x: 165, y: 105, label: 'NBO' },
              { x: 220, y: 90, label: 'DAC' },
            ].map((p, i) => (
              <g key={i}>
                <path d={`M ${p.x} ${p.y} Q ${(p.x + 190) / 2} ${Math.min(p.y, 80) - 20} 190 80`} fill="none" stroke="#06B6D4" strokeWidth="0.7" strokeOpacity="0.6" strokeDasharray="2 2">
                  <animate attributeName="stroke-dashoffset" from="0" to="-8" dur="2s" repeatCount="indefinite" />
                </path>
                <circle cx={p.x} cy={p.y} r="2" fill="#06B6D4" />
                <text x={p.x + 4} y={p.y + 3} fontSize="6" fill="#06B6D4" opacity="0.7" fontFamily="monospace">{p.label}</text>
              </g>
            ))}
            <text x="195" y="78" fontSize="6" fill="#F59E0B" fontFamily="monospace">DXB</text>
          </svg>
        </div>
      </div>

      {/* URGENCY BUCKETS */}
      <div className={tileBase}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-white/60 flex items-center gap-1.5"><Activity className="h-3 w-3" /> Urgency Buckets</span>
          <span className="text-[10px] font-mono text-white/50">days waiting</span>
        </div>
        <div className="flex items-end gap-1.5 h-20">
          {urgency.map((b, i) => {
            const max = Math.max(...urgency.map((x) => x.count), 1);
            const pct = (b.count / max) * 100;
            const col = i < 2 ? '#94A3B8' : i < 4 ? '#F59E0B' : '#EF4444';
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[10px] font-mono text-white/70">{b.count}</span>
                <div className="w-full rounded-sm" style={{ height: `${pct || 4}%`, backgroundColor: col, boxShadow: `0 0 8px ${col}66` }} />
                <span className="text-[8px] font-mono text-white/40">{b.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* NATIONALITIES */}
      <div className={tileBase}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-white/60 flex items-center gap-1.5"><BarChart3 className="h-3 w-3" /> Nationalities</span>
          <span className="text-[10px] font-mono text-white/50">{breakdown.reduce((a, b) => a + b.count, 0)} total</span>
        </div>
        <div className="space-y-1.5">
          {breakdown.map((b, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="text-base">{b.flag}</span>
              <span className="font-mono text-white/70 w-20 truncate">{b.nat.slice(0, 12)}</span>
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${b.pct}%`, backgroundColor: ['#06B6D4', '#10B981', '#F59E0B', '#3B82F6', '#A855F7'][i] || '#94A3B8' }} />
              </div>
              <span className="font-mono text-white/80 text-[11px] w-10 text-right">{b.pct}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* REFUND TIMELINE */}
      <div className={tileBase}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-white/60 flex items-center gap-1.5"><RotateCcw className="h-3 w-3 text-amber-400" /> Refund Timeline</span>
          <span className="text-[10px] font-mono text-amber-300/70">7 DAYS</span>
        </div>
        <Sparkline data={refundSeries} color="#F59E0B" height={36} />
      </div>
    </div>
  );
};

/* ===========================================================================
   OpsSearchBar
=========================================================================== */
export const OpsSearchBar = ({ value, onChange, count, bestScore }) => (
  <div className="relative">
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
      <input
        type="text"
        placeholder=">> SEARCH NAME · PASSPORT · CONTRACT #..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 bg-black/40 border border-white/10 rounded-xl pl-10 pr-10 font-mono text-sm tracking-wider text-white placeholder:text-white/30 focus:outline-none focus:border-amber-500/50 focus:ring-2 focus:ring-amber-500/10 transition"
        data-testid="ops-search-input"
      />
      {value && (
        <button onClick={() => onChange('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
    {value && (
      <div className="mt-2 flex items-center justify-between text-[11px] font-mono">
        <span className="text-amber-300 flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" />
          {count > 0 ? `${count} MATCH${count > 1 ? 'ES' : ''} FOUND` : 'NO MATCHES'}
        </span>
        {bestScore > 0 && <span className="text-white/40">BEST · {Math.round(bestScore)}%</span>}
      </div>
    )}
  </div>
);
